import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  fetchSecurityDepositById,
  createSecurityDeposit,
  updateSecurityDeposit,
  SecurityDeposit,
} from './SecurityDepositApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSave,
  faTimes,
  faFileAlt,
} from '@fortawesome/free-solid-svg-icons';

const initialForm: SecurityDeposit = {
  contractId: '',
  depositType: '',
  instrumentNo: '',
  amount: 0,
  depositCreatedDate: '',
  depositSubmitedDate: '',
  bankAccountNo: '',
  bankName: '',
  accountHolderName: '',
  bankAddress: '',
  status: '',
  comment: '',
  depositeReturnedDate: '',
  depositTransferedOwnAcDate: '',
};

const SecurityDepositForm: React.FC = () => {
  const { contractId, depositId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const isViewMode = !!(depositId && !location.pathname.includes('/edit'));

  const [formData, setFormData] = useState<SecurityDeposit>({ ...initialForm, contractId: contractId! });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (depositId) {
      loadDeposit();
    }
  }, [depositId]);

  const loadDeposit = async () => {
    setLoading(true);
    try {
      const response = await fetchSecurityDepositById(contractId!, depositId!);
      setFormData(response.data);
    } catch (err) {
      setError('Failed to load security deposit.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (depositId) {
        await updateSecurityDeposit(contractId!, depositId!, formData);
      } else {
        await createSecurityDeposit(contractId!, formData);
      }
      navigate(`/contracts/${contractId}/security-deposit`);
    } catch (err) {
      setError('Failed to save deposit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
        <FontAwesomeIcon icon={faFileAlt} />
        {isViewMode
          ? 'View Security Deposit'
          : depositId
          ? 'Edit Security Deposit'
          : 'Add Security Deposit'}
      </h2>

      {error && <div className="text-red-500 mb-4">{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { label: 'Deposit Type', name: 'depositType', type: 'text' },
            { label: 'Instrument No', name: 'instrumentNo', type: 'text' },
            { label: 'Amount', name: 'amount', type: 'number' },
            { label: 'Deposit Created Date', name: 'depositCreatedDate', type: 'date' },
            { label: 'Deposit Submitted Date', name: 'depositSubmitedDate', type: 'date' },
            { label: 'Bank Account No', name: 'bankAccountNo', type: 'text' },
            { label: 'Bank Name', name: 'bankName', type: 'text' },
            { label: 'Account Holder Name', name: 'accountHolderName', type: 'text' },
            { label: 'Bank Address', name: 'bankAddress', type: 'text' },
            { label: 'Returned Date', name: 'depositeReturnedDate', type: 'date' },
            { label: 'Transfer to Own A/C Date', name: 'depositTransferedOwnAcDate', type: 'date' },
          ].map(({ label, name, type }) => (
            <div key={name}>
              <label className="block text-sm font-medium">{label}</label>
              <input
                type={type}
                name={name}
                value={(formData as any)[name] || ''}
                onChange={handleChange}
                className="border px-3 py-2 rounded w-full"
                disabled={!!isViewMode}
              />
            </div>
          ))}

          <div>
            <label className="block text-sm font-medium">Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="border px-3 py-2 rounded w-full"
              disabled={!!isViewMode}
            >
              <option value="">Select Status</option>
              <option value="SUBMITTED">Submitted</option>
              <option value="RETURNED">Returned</option>
              <option value="PENDING">Pending</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium">Comment</label>
          <textarea
            name="comment"
            value={formData.comment}
            onChange={handleChange}
            rows={3}
            className="border px-3 py-2 rounded w-full"
            disabled={!!isViewMode}
          ></textarea>
        </div>

        <div className="flex justify-end gap-4 mt-6">
          <button
            type="button"
            onClick={() => navigate(`/contracts/${contractId}/security-deposit`)}
            className="bg-gray-500 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-gray-600"
          >
            <FontAwesomeIcon icon={faTimes} />
            Cancel
          </button>

          {!isViewMode && (
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-blue-700"
              disabled={loading}
            >
              <FontAwesomeIcon icon={faSave} />
              {depositId ? 'Update' : 'Save'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default SecurityDepositForm;